 /**
  * global papaLp: {
  *  config: {
  *    send_to_labels: string[]
  *  }
  * }
  */
import { handleMediaChange } from '../utils/matchMedia';
import { waitForSharedComponents } from '../utils/waitForSharedComponents';
import { locale } from '../utils/getLocale';

/**
 * Update image src based on screen size
 */
function updateImageSrc(img, size) {
  const mobileSrc = img.dataset.mobileSrc;
  const desktopSrc = img.dataset.desktopSrc;
  
  if (!mobileSrc && !desktopSrc) return;
  
  const isMobile = size === 'mobile';
  const targetSrc = isMobile && mobileSrc ? mobileSrc : desktopSrc;
  
  if (targetSrc && img.src !== targetSrc) {
    img.src = targetSrc;
  }
}

/**
 * Handle CF7 form response
 */
async function handleFormResponse(event, isSuccess) {
  const form = event.target.closest('.wpcf7');
  if (!form) return;
  
  const submitBtn = form.querySelector('input[type="submit"]');
  if (submitBtn) {
    submitBtn.disabled = false;
  }
  
  // Remove existing response output
  const responseOutput = form.querySelector('.wpcf7-response-output');
  if (responseOutput) {
    responseOutput.remove();
  }
  
  // Hide loader if available
  if (typeof window.hideLoader === 'function') {
    window.hideLoader();
  }
  
  // Get API response
  const apiResponse = event.detail?.apiResponse;
  if (!apiResponse) return;
  
  // Verify form matches the response target
  if (apiResponse.into && !form.matches(apiResponse.into)) {
    return;
  }
  
  const message = apiResponse.message;
  
  // Wait for SharedComponents to be available
  let modalManager = null;
  try {
    await waitForSharedComponents();
    modalManager = window.SharedComponents.modalManager;
  } catch (error) {
    console.error('Failed to load SharedComponents:', error);
    return;
  }
  
  // Handle success
  if (isSuccess) {

    //trigger analytics event
    try {
      gtag('event', 'form-submitted', {
        'event_category': 'form',
        'event_label': 'form_submitted',
        'value': 1
      });
      

      if(papaLp.send_to_labels.length > 0) {
        papaLp.send_to_labels.forEach(label => {
          gtag('event', 'conversion', {
            'event_category': 'form',
            'send_to': label,
            'value': 1
          });
        });
      }

    } catch (error) {   
      console.error('Failed to trigger Google Analytics event:', error);
    }
 

    try { 
      fbq('trackCustom', 'form-submitted', {
        content_category: 'form',
        value: 1
      });
    } catch (error) {
      console.error('Failed to trigger Facebook Pixel event:', error);
    }

    modalManager.open({
      title: locale('Thank you!'),
      content: `<div class="modal-message">${message}</div>`,
      size: 'small',
      closeOnOverlay: true
    });
    return;
  }
  
  // Handle errors
  // Focus on first invalid field after modal closes
  const focusOnError = () => {
    const firstInvalid = form.querySelector(
      'input.wpcf7-not-valid, select.wpcf7-not-valid, textarea.wpcf7-not-valid'
    );
    if (firstInvalid) {
      firstInvalid.focus();
    }
  };
  
  modalManager.open({
    title: locale('Notice'),
    content: `<div class="modal-message modal-message--error">${message}</div>`,
    size: 'small',
    closeOnOverlay: true,
    onClose: focusOnError
  });
}

/**
 * Initialize CF7 form handlers
 */
function initFormHandlers(block) {
  const wpcf7 = block.querySelector('.wpcf7');
  if (!wpcf7 || wpcf7.dataset.formInitialized === '1') return;
  
  // Mark as initialized
  wpcf7.dataset.formInitialized = '1';
  
  const form = wpcf7.querySelector('form');
  const submitBtn = form?.querySelector('input[type="submit"]');
  
  // Show loader on submit
  if (submitBtn) {
    submitBtn.addEventListener('click', () => {
      if (typeof window.showLoader === 'function') {
        window.showLoader();
      }
    });
  }
  
  // Listen to CF7 events
  wpcf7.addEventListener('wpcf7mailfailed', (event) => handleFormResponse(event, false));
  wpcf7.addEventListener('wpcf7spam', (event) => handleFormResponse(event, false));
  wpcf7.addEventListener('wpcf7invalid', (event) => handleFormResponse(event, false));
  wpcf7.addEventListener('wpcf7mailsent', (event) => handleFormResponse(event, true));
  
  // Handle branch email selection (if present)
  const selectBranch = form?.querySelector('[name="select_branch"]');
  const branchMail = form?.querySelector('[name="branch_mail"]');
  
  if (selectBranch && branchMail) {
    selectBranch.addEventListener('change', (event) => {
      branchMail.value = event.target.value;
    });
  }
}
 
/**
 * Initialize all contact blocks on the page
 */
function init() {
  const blocks = document.querySelectorAll('.wp-block-papa-lp-contact');
  
  blocks.forEach((block) => {
    // Initialize image responsive behavior
    const img = block.querySelector('.contact__image-img');
    if (img && img.dataset.initialized !== '1') {
      img.dataset.initialized = '1';
      handleMediaChange((size) => updateImageSrc(img, size));
    }
    
    // Initialize form handlers
    initFormHandlers(block);
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// Export for manual re-initialization (e.g., after AJAX content load)
window.PapaLpContactInit = init;
